import type { GameContext } from "./types.ts";
import { tickLogicBombs, tickHijackedDamage } from "./cardEffectHandlers.ts";

/**
 * Per-frame timing accumulators for the sim-update sub-steps. The fields are a
 * subset of `renderPipeline`'s `perfAcc`, so that object can be passed straight
 * in (structural). When `null` is passed instead, the step runs untimed and no
 * `performance.now()` is read — byte-identical to the old non-perf branch.
 */
export interface SimStepPerf {
  waveMs: number;
  combatMs: number;
  contagionMs: number;
  clusterMs: number;
  synergyMs: number;
  scoreMs: number;
  breachMs: number;
  spawnDetectMs: number;
  virusTowerUpdates: number;
}

export interface SimStep {
  /**
   * Advance the simulation one fixed step. `perf` is the timing accumulator
   * (perf-toggle ON) or `null` (OFF). The update ORDER and side-effects are the
   * sole determinism contract — do NOT reorder; the golden master witnesses it.
   */
  update(dt: number, perf: SimStepPerf | null): void;
}

/**
 * BL-91 — the single owner of the master sim-update sequence.
 *
 * This sequence used to be DUPLICATED across `renderPipeline.update`'s
 * perf-timed and untimed branches (S115-F14): wave → combat (+ payload ticks) →
 * synergy → score → breach/path-reveal → spawn-detection, plus the per-sub-step
 * `performance.now()` sampling. Extracting it here kills the shotgun-surgery
 * hazard (the order now lives in ONE place) and gets the sim step out of a file
 * named `renderPipeline`. `renderPipeline` keeps the OUTER timing wrapper (the
 * frame-budget check, `perfAcc.frames++`, `flushPerfSample`); only the SEQUENCE
 * moved. Behavior-preserving — the post-BL-99 baseline stays byte-identical.
 */
export function createSimStep(ctx: GameContext): SimStep {
  const {
    gs,
    eventBus,
    waveManager,
    combatSystem,
    synergySystem,
    scoreSystem,
    breachCinematic,
    pathRevealAnimation,
    spawnDetectionSystem,
  } = ctx;

  function update(dt: number, perf: SimStepPerf | null): void {
    // F2 / DD-14.7-5: spawn queue PAUSED during breach cinematic. Existing
    // enemies still move (via combatSystem.update below) — cinematic is a
    // pressure amplifier, not a freeze.
    const tWave = perf ? performance.now() : 0;
    if (!combatSystem.timeWarpActive && !breachCinematic.isActive()) {
      waveManager.update(dt);
    } else if (!combatSystem.timeWarpActive) {
      // Still advance enemy positions even when spawn-paused.
      waveManager.updateEnemiesOnly(dt);
    } else {
      // GA-41: time warp freezes spawns AND enemy movement — but it must not
      // freeze the QUESTION "is this wave over?". The completion test used to
      // live only inside the enemy tick, which this branch skips, so a wave
      // whose last enemy died just before the warp could not complete until
      // the warp expired. Harmless while GA-40 kept the warp under one frame;
      // a real multi-second stall once GA-40 is fixed. No enemy is ticked here
      // — the freeze is preserved, only the completion test runs.
      // B7: pass dt so the wave-level deadlock backstop keeps ticking while
      // the wave is FROZEN — the exact state in which a deadlock is otherwise
      // invisible, since nothing else in this branch advances.
      waveManager.checkWaveCompletion(false, dt);
    }
    if (perf) perf.waveMs += performance.now() - tWave;

    const tCombat = perf ? performance.now() : 0;
    combatSystem.update(dt, gs.lives);
    // BL-70: per-frame payload ticks (dormant = no-op)
    tickLogicBombs(dt, gs.logicBombs, waveManager.enemies, eventBus);
    tickHijackedDamage(dt, waveManager.enemies, eventBus);
    if (perf) {
      perf.combatMs += performance.now() - tCombat;
      const combatPerf = combatSystem.consumePerfFrame();
      perf.contagionMs += combatPerf.contagionMs;
      perf.clusterMs += combatPerf.clusterMs;
      perf.virusTowerUpdates += combatPerf.virusTowerUpdates;
    }

    const tSynergy = perf ? performance.now() : 0;
    synergySystem.update(
      dt,
      waveManager.enemies,
      gs.cascadeActive ? gs.cascadeDamage : 0, // CASCADE (BL-72)
    );
    if (perf) perf.synergyMs += performance.now() - tSynergy;

    const tScore = perf ? performance.now() : 0;
    scoreSystem.update(dt);
    if (perf) perf.scoreMs += performance.now() - tScore;

    const tBreach = perf ? performance.now() : 0;
    // CR-29: Breach cinematic update
    breachCinematic.update(dt);
    // CR2: Path reveal animation update
    pathRevealAnimation.update(dt);
    if (perf) perf.breachMs += performance.now() - tBreach;

    const tDetect = perf ? performance.now() : 0;
    // Signal tower spawn detection (runs during build + combat)
    spawnDetectionSystem.update(gs.towers, 1);
    if (perf) perf.spawnDetectMs += performance.now() - tDetect;
  }

  return { update };
}
